import { useId, useState } from 'react';
import { createPortal } from 'react-dom';

import { QuantityStepper } from './QuantityStepper';
import { RipButton } from './RipButton';
import { StripePaymentModal } from './StripePaymentModal';

export interface BottomActionProps {
  quantity: number;
  onQuantityChange: (next: number) => void;
  onBuy: () => void;
  /** Per-unit price (number like 25 or string like "25.0"). */
  unitPrice: number | string;
  /** Minimum / maximum quantity the user may pick. */
  minQty?: number;
  maxQty?: number;
}

function formatPrice(totalCents: number): string {
  if (Number.isInteger(totalCents / 100)) return `$${totalCents / 100}`;
  return `$${(totalCents / 100).toFixed(2)}`;
}

interface BarProps {
  quantity: number;
  minQty: number;
  maxQty: number;
  onQuantityChange: (next: number) => void;
  onBuy: () => void;
  priceLabel: string;
  disabled: boolean;
}

// Shell background is a 2-stop linear gradient. The direction lives in its
// own CSS var so the operator can flip horizontal/vertical from the
// VariantPicker without our React code observing the var. When both stops
// hold the same colour, the gradient renders as a solid fill — which is
// what classic uses by default (white).
const SHELL_BG =
  'linear-gradient(' +
  'var(--co-BottomActionShell-gradientDirection, to bottom), ' +
  'var(--co-BottomActionShell-gradientFrom, #ffffff), ' +
  'var(--co-BottomActionShell-gradientTo, #ffffff))';
const SHELL_BORDER = 'var(--co-BottomActionShell-border, rgba(171,174,179,0.24))';

/**
 * Mobile rail — edge-to-edge, fixed to viewport bottom.
 *
 * Shape = two overlapping white divs, each shaped by its own `clip-path`:
 *
 *   1. Wave (left/centre): a full-size white div clipped by a quadratic
 *      Bézier curve that SAGS in the middle (edges at y≈54.7%, centre
 *      drops to y≈61.7%, then rises back). Uses SVG `<clipPath>` because
 *      CSS `ellipse()` can only bulge outward — this curve is concave.
 *
 *   2. Plateau (right): a rectangle sized 69.6%×105.97% clipped by
 *      `inset(0 round 110px 0 0 0)` — rounded top-left corner only,
 *      matching Figma's `rounded-tl-[110px]`. Gradient white → #F4F4F4.
 *
 * `overflow-hidden` on the rail container clips anything that spills
 * past 0–100%. Shadow is a CSS `drop-shadow()` on the outer wrapper so
 * it follows the composite silhouette, not each piece separately.
 *
 * All coordinates are percentages of the 375×201 Figma frame, so the
 * shape scales naturally with rail width.
 */
function MobileBar({
  quantity,
  minQty,
  maxQty,
  onQuantityChange,
  onBuy,
  priceLabel,
  disabled,
}: BarProps) {
  // useId so two mounted copies (mobile+desktop wrappers) don't collide.
  const clipId = `rail-wave-${useId().replace(/:/g, '')}`;

  // Render into <body> so the rail is guaranteed to be positioned against
  // the viewport (no ancestor `transform` / `filter` / `contain` can hijack
  // its containing block). `min-[900px]:hidden` is a viewport media query
  // — container queries would not resolve in the body subtree.
  return createPortal(
    <div
      data-co-component="BottomActionShell"
      className="min-[900px]:hidden fixed bottom-0 left-0 right-0 z-30"
      style={{ filter: 'drop-shadow(0 -6px 20px rgba(0,0,0,0.18))' }}
    >
      <div className="relative mx-auto h-[201px] w-full max-w-[1400px] overflow-hidden">
        {/* SVG defs — invisible, just hosts the wave clipPath */}
        <svg
          aria-hidden
          width="0"
          height="0"
          className="absolute"
          style={{ position: 'absolute' }}
        >
          <defs>
            <clipPath id={clipId} clipPathUnits="objectBoundingBox">
              {/* Quadratic Bézier sag: edges y=0.547, centre sags to y=0.617 */}
              <path d="M 0 0.547 Q 0.5 0.687 1 0.547 L 1 1 L 0 1 Z" />
            </clipPath>
          </defs>
        </svg>

        {/* 1. Wave — clipped by SVG <clipPath> (concave curve) */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ clipPath: `url(#${clipId})`, background: SHELL_BG }}
        />

        {/* 2. Plateau — clipped by CSS inset with TL rounded corner */}
        <div
          aria-hidden
          className="absolute bg-gradient-to-b from-white to-[#F4F4F4]"
          style={{
            left: '57.07%',
            top: '31.34%',
            width: '69.6%',
            height: '105.97%',
            clipPath: 'inset(0 round 110px 0 0 0)',
          }}
        />

        <div className="absolute left-4 bottom-[50px]">
          <QuantityStepper
            value={quantity}
            min={minQty}
            max={maxQty}
            onChange={onQuantityChange}
            size="md"
          />
        </div>

        <div className="absolute right-[5px] bottom-[20px]">
          <RipButton
            onClick={onBuy}
            count={quantity}
            priceLabel={priceLabel}
            size="md"
            disabled={disabled}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Desktop rail — CSS-only, static, sits inside the sidebar column.
 * Uses the sidebar's own width; RIP button extends below the rail.
 */
function DesktopBar({
  quantity,
  minQty,
  maxQty,
  onQuantityChange,
  onBuy,
  priceLabel,
  disabled,
}: BarProps) {
  return (
    <div
      data-co-component="BottomActionShell"
      className="hidden @[900px]:block relative min-h-[220px] w-full"
    >
      <div
        aria-hidden
        className="
          absolute inset-x-0 top-0 h-[127px]
          rounded-tl-[28px] rounded-tr-[28px]
          rounded-bl-[68px] rounded-br-[28px]
          [box-shadow:0_64px_34px_0_rgba(0,0,0,0.05)]
        "
        style={{
          background: SHELL_BG,
          border: `var(--co-BottomActionShell-borderWidth, 2px) solid ${SHELL_BORDER}`,
        }}
      />
      <div className="absolute left-[14px] top-[14px]">
        <QuantityStepper
          value={quantity}
          min={minQty}
          max={maxQty}
          onChange={onQuantityChange}
          size="lg"
        />
      </div>
      <div className="absolute right-[12px] top-[10px]">
        <RipButton
          onClick={onBuy}
          count={quantity}
          priceLabel={priceLabel}
          size="lg"
          disabled={disabled}
        />
      </div>
    </div>
  );
}

/**
 * Pack-page bottom action.
 *
 *   < 900px (mobile) — `fixed bottom-0`, edge-to-edge, SVG rail.
 *   ≥ 900px (desktop) — `static`, CSS-only rail inside sidebar.
 *
 * Each variant renders in its own branch — the caller wraps this
 * component with `@[900px]:hidden` / `hidden @[900px]:block` at the
 * layout level, and the internal `@[900px]:hidden` / `@[900px]:block`
 * on each branch guarantees only one bar is ever visible even if both
 * copies are mounted in the DOM.
 */
export function BottomAction({
  quantity,
  onQuantityChange,
  onBuy: _onBuy,
  unitPrice,
  minQty = 1,
  maxQty = 99,
}: BottomActionProps) {
  const [stripeOpen, setStripeOpen] = useState(false);

  const unitCents = Math.round(Number(unitPrice) * 100) || 0;
  const totalCents = unitCents * Math.max(quantity, 1);
  const outOfStock = maxQty <= 0;
  const priceLabel = outOfStock ? 'Sold out' : formatPrice(totalCents);

  const shared: BarProps = {
    quantity,
    minQty,
    maxQty,
    onQuantityChange,
    onBuy: () => setStripeOpen(true),
    priceLabel,
    disabled: outOfStock,
  };

  return (
    <>
      <MobileBar {...shared} />
      <DesktopBar {...shared} />
      {stripeOpen && createPortal(
        <StripePaymentModal
          priceLabel={priceLabel}
          amountCents={totalCents}
          onClose={() => setStripeOpen(false)}
          onSuccess={_onBuy}
        />,
        document.body,
      )}
    </>
  );
}
